import { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import { Avatar, AVATAR_SIZES } from 'ui/avatar';
import { body2, colors, fontWeights, spacing } from 'ui/theme';
import { getFistAndLastNameFromFullName } from 'utils/stringHelpers';

const UserActionContainer = styled.div`
  ${body2};
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
`;

const UserActionContent = styled.div<{
  $justify?: UserActionProps['justify'];
}>`
  display: flex;
  flex-direction: column;
  ${({ $justify }) =>
    $justify === 'start'
      ? css`
          flex: 1;
        `
      : css``}
`;

const UserActionLabel = styled.div<{
  $justify?: UserActionProps['justify'];
}>`
  font-weight: ${fontWeights.bold};
  display: flex;
  margin-right: ${spacing.plusHalf};
  ${({ $justify }) =>
    $justify === 'start'
      ? css`
          flex: 1;
        `
      : css`
          width: 100%;
          max-width: 65px;
          margin-right: ${spacing.x3};
        `}
`;

const AvatarContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  ${body2};
  color: ${colors.secondaryText};
  white-space: normal;
`;

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const UserActionDate = styled.div`
  ${body2};
  color: ${colors.grey80};
  margin-bottom: ${spacing.half};
`;

type UserActionProps = {
  fullName: string;
  imgUrl: string;
  title: string;
  date: string;
  justify?: 'start' | 'space-between';
};

export const UserAction = ({
  title,
  date,
  fullName,
  imgUrl,
  justify = 'start',
  ...rest
}: PropsWithChildren<UserActionProps>) => {
  const { firstName, lastName } = getFistAndLastNameFromFullName(fullName || '');

  return (
    <UserActionContainer {...rest}>
      <UserActionLabel $justify={justify}>{title}</UserActionLabel>
      <UserActionContent $justify={justify}>
        <UserActionDate>{date}</UserActionDate>
        <AvatarContainer>
          <StyledAvatar
            firstName={firstName}
            lastName={lastName}
            imgSrc={imgUrl}
            size={AVATAR_SIZES.SMALL}
            style={{ marginRight: spacing.half }}
            data-test="avatar"
          />
          {fullName}
        </AvatarContainer>
      </UserActionContent>
    </UserActionContainer>
  );
};
